import React from "react";
import styled, { keyframes } from "styled-components";
import { Device } from "../../../components/device";
import ImageCrop from "../UI/Image/ImageCrop";
import Anchor from "../../../components/Router/Anchor";

/*
The image component creates one of these two images:

- And image as is, with its natural height and size.
- An aspect ratio placeholder, that mimics the original aspect ratio of the image

Properties:
- Responsive images (5 sizes: xs, sm, md, lg, xl)
- Alt text
- Caption text
- Width (Natural or aspect ratio)
- Height (Natural or aspect ratio)
- Aspect ratio (If selected, it creates an aspect ratio placeholder)

*/

//Future improvements
//Loading only one source, and using browser size compression to load only the neccessary size
//- LQIP

/*
//Usage:
<Image
    src="https://via.placeholder.com/320x320?text=XSmall"
    srcSm="https://via.placeholder.com/768x300?text=Small"
    srcMd="https://via.placeholder.com/1024x300?text=Medium"
    srcLg="https://via.placeholder.com/1280x300?text=Large"
    srcXl="https://via.placeholder.com/1920x800?text=XLarge"
    alt="Alt text here"
    caption="Caption text comes here"
    width={1}
    height={1}
    aspectRatio />
*/

interface FigureProps
{
    alt?: string;
    src: string;
    caption?: string;
    heightRatio?: number;
    link?: string;
}

interface SizeProps
{
    heightRatioPercentage: string;
}
const FadeIn = keyframes`
     0% { opacity: 0; }
     100% { opacity: 1; }
`;

const FigureWrap = styled.figure`
    animation: ${FadeIn} .5s forwards;
    position: relative;
    margin-bottom: 1rem;
    ${Device.laptop} {
        &:hover {
            .figure__caption {
                opacity: 1;
            }
        }
    }
    //if text module follows
    & + .text-wrap {
        padding-top: 1rem;
    }
`;

const FigurePlaceholder = styled.div<SizeProps>`
    height: 0;
    overflow: hidden;
    padding-top: ${props => props.heightRatioPercentage}%;
    position: relative;

    img{
        display: block;
        max-width: 100%;
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        object-fit: cover;
    }
`;

const FigureImage = styled.img`
    display: inline-block;
    max-width: 100%;
`;

const FigureCaption = styled.figcaption`
    display: block;
    padding-top: .5rem;
    padding-bottom: .5rem;
    font-size: 0.7333rem;
    font-weight: 500;
    position: absolute;
    left: 0;
    bottom: 0;
    padding-left: 1rem;
    padding-right: 1rem;
    text-shadow: 0 0 63px rgba(0,0,0,.3);
    width: 100%;

    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, .5) 100%);
    color: var(--color-gray-10, white);
    ${Device.laptop} {
        opacity: 0;
        transition: opacity .3s;
    }
`;

const Figure: React.FunctionComponent<FigureProps> = (props) =>
{
    const { heightRatio, alt, src, caption } = props;
    //Set image size (not used at the time)
    const figurePlaceholder = React.createRef<HTMLDivElement>();

    const renderImage = () =>
    {
        return (
            <FigureWrap>
                {heightRatio ?
                    (
                        <FigurePlaceholder ref={figurePlaceholder} heightRatioPercentage={(heightRatio * 100).toFixed(2)}>
                            <ImageCrop alt={alt} src={src} resizeMode="crop" />
                        </FigurePlaceholder>
                    ) : (
                        <FigureImage alt={alt} src={src} />
                    )}
                {caption ? <FigureCaption className="figure__caption">{caption}</FigureCaption> : null}
            </FigureWrap>
        );
    };

    const renderLinkImage = () =>
    {
        if (props.link)
        {
            return <Anchor to={props.link}>{renderImage()}</Anchor>;
        }
        return renderImage();
    };

    return renderLinkImage();
};

export default Figure;